import type { AppTheme } from './types';

/**
 * Palette entries whose value depends on the colour scheme: text, surfaces,
 * borders and interaction states. One the base theme sets describes the light
 * scheme only, so the dark scheme takes its own from `darkTheme` or the dark
 * defaults rather than inheriting it. Brand and status colours are not here:
 * picking one once applies to both schemes.
 */
export const schemePaletteKeys = ['text', 'background', 'divider', 'action', 'monochrome', 'white', 'black', 'outlinedBorder', 'filledInputBackground', 'standardInputLine', 'snackbarBackground', 'containerBackground'] as const;
const isSchemeKey = (key: string) => (schemePaletteKeys as readonly string[]).includes(key);

/**
 * Splits a theme into what both schemes share and the palette entries that
 * belong to the scheme it was written for (see `schemePaletteKeys`).
 */
export function splitSchemePalette(theme: AppTheme): {
  shared: AppTheme;
  scheme: AppTheme;
} {
  const {
    palette,
    ...rest
  } = theme;
  if (!palette) {
    return {
      shared: theme,
      scheme: {}
    };
  }
  const entries = Object.entries(palette);
  const sharedEntries = entries.filter(([key]) => !isSchemeKey(key));
  const schemeEntries = entries.filter(([key]) => isSchemeKey(key));
  return {
    shared: sharedEntries.length ? {
      ...rest,
      palette: Object.fromEntries(sharedEntries)
    } : rest,
    scheme: schemeEntries.length ? {
      palette: Object.fromEntries(schemeEntries)
    } : {}
  };
}